import React, { useEffect, useRef, useCallback, useState } from "react";
import styled from "styled-components";
import Text from "new_components/atoms/Text";
import TextInput from "new_components/molecules/TextInput";
import ToggleItem from "new_components/molecules/ToggleItem";
import SelectBox from "new_components/molecules/SelectBox";
import CategorySelector from "./CategorySelector";
import CategoryOptionsEditor from "./CategoryOptionsEditor"; // Make sure *this* file also avoids passing the event
import colors from "styles/colors";
import typography from "styles/typography";
import EmojiPicker from 'emoji-picker-react';

// Custom styling for emoji picker
const customStyles = `
  /* Hide the skin tone selector in emoji picker */
  .emoji-picker-react .emoji-categories .emoji-group[data-name="Skin Tones"] {
    display: none !important;
  }
  
  /* Make the emoji picker fill the available width */
  .emoji-picker-react {
    width: 100% !important;
  }
  
  /* Hide the emoji skin tone button */
  aside.emoji-picker-react .skin-tones-list {
    display: none !important;
  }
`;

// Add the custom styles to the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = customStyles;
  document.head.appendChild(style);
}

// Category type options
const categoryTypeOptions = [
  { label: "Text", value: "text" },
  { label: "Date", value: "date" }
];

/**
 * Custom component for emoji selection button
 */
const EmojiButton = ({ emoji, onClick }) => {
  return (
    <StyledEmojiButton 
      onClick={(e) => {
        console.log("EmojiButton clicked");
        onClick(e);
      }}
    >
      {emoji ? emoji : '+'} 
    </StyledEmojiButton>
  );
};

/**
 * Custom component to handle scale dropdown selection
 */
const ScaleDropdown = ({ scale, onChange, label, options }) => {
  // Create options array in the format SelectBox expects
  const selectOptions = options.map(opt => ({
    label: String(opt),
    value: opt
  }));

  // Convert current scale to string for direct comparison
  const stringScale = String(scale);
  
  return (
    <div style={{ position: 'relative' }}>
      <select 
        value={stringScale} 
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          height: '40px',
          width: '100%',
          padding: '8px 12px',
          border: `1px solid ${colors.neutral[300]}`,
          borderRadius: '4px',
          backgroundColor: 'white',
          fontSize: '14px',
          fontFamily: 'Raleway, sans-serif'
        }}
      >
        {options.map(option => (
          <option key={option} value={String(option)}>
            {option}
          </option>
        ))}
      </select>
      <label
        style={{
          position: 'absolute',
          top: '-8px',
          left: '8px',
          fontSize: '12px',
          backgroundColor: 'white',
          padding: '0 4px',
          color: colors.text.dark
        }}
      >
        {label}
      </label>
    </div>
  );
};

/**
 * Custom Emoji Picker component with some options hidden
 */
const CustomEmojiPicker = ({ onEmojiClick }) => {
  // Add effect to hide skin tone selectors with CSS after component renders
  useEffect(() => {
    // Function to add CSS to hide skin tone elements
    const hideSkinTones = () => {
      // Target elements from emoji-picker-react that handle skin tones
      const skinToneButtons = document.querySelectorAll('.emoji-picker-react .skin-tone-button');
      if (skinToneButtons.length) {
        skinToneButtons.forEach(el => {
          if (el) el.style.display = 'none';
        });
      }
    };
    
    // Run once on mount
    hideSkinTones();
    
    // Also run after a short delay to ensure elements have rendered
    const timeoutId = setTimeout(hideSkinTones, 200);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  return (
    <div style={{ width: '100%' }}>
      <EmojiPicker
        onEmojiClick={onEmojiClick}
        searchPlaceholder="Search emojis..."
        previewConfig={{ showPreview: true }}
        width="100%"
        height="350px"
        skinTonesDisabled
      />
    </div>
  );
};

const PropertyEditor = ({
  properties,
  onChange,            // (propName, newValue) => void
  definition,
  componentType,
  categories,
  onCategoriesChange,  // setCategories
}) => {
  // Keep track of the previous category to detect actual changes
  const prevCategoryRef = useRef(properties.category);
  // Keep track of the previous scale to detect changes
  const prevScaleRef = useRef(properties.scale);
  
  // State for adding a new category and managing its type
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryType, setNewCategoryType] = useState("text");
  const [showCategoryTypeSelect, setShowCategoryTypeSelect] = useState(false);
  
  // For multiSelect and date types, use dropdown's definition for UI rendering
  const renderingComponentType = ['multiSelect', 'date'].includes(componentType) ? 'dropdown' : componentType;
  
  // Simplified emoji picker approach
  const [activeEmojiRowIndex, setActiveEmojiRowIndex] = useState(null);
  
  /**
   * Handle emoji selection
   */
  const handleEmojiClick = (emojiData, index) => {
    console.log("Emoji selected:", emojiData, "for index:", index);
    const { emoji } = emojiData;
    
    // Get the current labels array
    const currentScaleLabels = [...properties.scaleLabels || []];
    
    // Add emoji field if it doesn't exist
    if (!properties.scaleEmojis) {
      const initialEmojis = Array(currentScaleLabels.length).fill(null);
      onChange("scaleEmojis", initialEmojis);
    }
    
    // Get the current emojis array
    const currentEmojis = [...properties.scaleEmojis || []];
    
    // Ensure the array is the right size
    while (currentEmojis.length < currentScaleLabels.length) {
      currentEmojis.push(null);
    }
    
    // Update the emoji at the given index
    currentEmojis[index] = emoji;
    
    // Save the updated emojis
    onChange("scaleEmojis", currentEmojis);
    
    // Close the emoji picker
    setActiveEmojiRowIndex(null);
  };
  
  /**
   * Toggle emoji picker for a specific row
   */
  const toggleEmojiPicker = (index, event) => {
    // Prevent the default button click behavior
    event.preventDefault();
    event.stopPropagation();
    
    console.log("Toggle emoji picker for index:", index);
    
    // If this row is already active, close it
    if (activeEmojiRowIndex === index) {
      setActiveEmojiRowIndex(null);
    } else {
      // Otherwise, open it for this row
      setActiveEmojiRowIndex(index);
    }
  };
  
  /**
   * Toggle emoji mode
   */
  const toggleEmojiMode = (enabled) => {
    onChange("useEmojis", enabled);
    
    // If enabling emoji mode, initialize the emojis array if needed
    if (enabled && (!properties.scaleEmojis || !properties.scaleEmojis.length)) {
      const scaleLength = Number(properties.scale) || 5;
      const initialEmojis = Array(scaleLength).fill(null);
      onChange("scaleEmojis", initialEmojis);
    }
  };
  
  /**
   * Add an option to an existing category in the shared categories array
   */
  const addOptionToCategory = useCallback((categoryName, newOption) => {
    onCategoriesChange((prev) =>
      prev.map((cat) =>
        cat.name === categoryName
          ? { ...cat, options: [...cat.options, newOption] }
          : cat
      )
    );
  }, [onCategoriesChange]);

  /**
   * Remove an option from a category in the shared categories array
   */
  const removeOptionFromCategory = useCallback((categoryName, optionToRemove) => {
    onCategoriesChange((prev) =>
      prev.map((cat) =>
        cat.name === categoryName
          ? { 
              ...cat, 
              options: cat.options.filter(option => option !== optionToRemove) 
            }
          : cat
      )
    );
  }, [onCategoriesChange]);

  /**
   * Create a new category (if it doesn't exist) and set this component's "category" property
   */
  const addCategory = useCallback((newCategoryName, categoryType = "text") => {
    onCategoriesChange((prev) => {
      // If it doesn't exist, add it with the specified type
      if (!prev.some((cat) => cat.name === newCategoryName)) {
        return [...prev, { 
          name: newCategoryName, 
          options: [], 
          type: categoryType 
        }];
      }
      return prev;
    });
    // Also set the property to use this new category
    onChange("category", newCategoryName);
    
    // Reset the UI state
    setNewCategoryName("");
    setNewCategoryType("text");
    setShowCategoryTypeSelect(false);
  }, [onCategoriesChange, onChange]);

  /**
   * Handler for when a user wants to create a new category
   */
  const handleNewCategory = useCallback((name) => {
    setNewCategoryName(name);
    setShowCategoryTypeSelect(true);
  }, []);

  /**
   * Handler for confirming a new category with the selected type
   */
  const handleConfirmNewCategory = useCallback(() => {
    if (newCategoryName) {
      addCategory(newCategoryName, newCategoryType);
    }
  }, [newCategoryName, newCategoryType, addCategory]);

  /**
   * Memoized handler for the options change - used by selectOptions type
   */
  const handleOptionsChange = useCallback(
    (propName, newOptions) => {
      // First, update the options array
      onChange(propName, newOptions);
      
      // Then check if we need to update the "Add 'Other (specify own)' option" toggle
      const hadOtherOption = properties.options?.includes("Other (specify own)");
      const hasOtherOption = newOptions?.includes("Other (specify own)");
      
      // If the "Other" option was removed directly from the options list,
      // also turn off the toggle switch
      if (hadOtherOption && !hasOtherOption) {
        onChange("Add 'Other (specify own)' option", false);
      }
    },
    [onChange, properties.options]
  );

  /**
   * Update scale labels when scale changes
   */
  useEffect(() => {
    // Convert both values to numbers for proper comparison
    const currentScale = Number(properties.scale);
    const prevScale = Number(prevScaleRef.current);
    
    console.log('Scale change effect:', { 
      currentScale, 
      prevScale, 
      componentType,
      scaleLabels: properties.scaleLabels
    });
    
    // Only run for likert or slider components and when scale actually changes
    if ((componentType === 'likert' || componentType === 'slider') && 
        currentScale !== prevScale && !isNaN(currentScale)) {
      
      // Update reference with the numeric value
      prevScaleRef.current = properties.scale;
      
      // Generate default labels for the new scale
      const newScale = parseInt(currentScale, 10);
      const currentLabels = Array.isArray(properties.scaleLabels) ? 
        [...properties.scaleLabels] : [];
      
      let newLabels = [];
      
      // If it's a 5-point scale
      if (newScale === 5) {
        newLabels = [
          "Strongly Disagree",
          "Disagree", 
          "Neutral", 
          "Agree", 
          "Strongly Agree"
        ];
        console.log('Created 5-point labels:', [...newLabels]);
      } 
      // If it's a 7-point scale
      else if (newScale === 7) {
        newLabels = [
          "Strongly Disagree",
          "Moderately Disagree", 
          "Slightly Disagree",
          "Neutral", 
          "Slightly Agree",
          "Moderately Agree", 
          "Strongly Agree"
        ];
        console.log('Created 7-point labels:', [...newLabels]);
      } 
      // For other scales
      else {
        // Create generic labels
        for (let i = 0; i < newScale; i++) {
          newLabels[i] = `Option ${i + 1}`;
        }
      }
      
      // IMPORTANT CHANGE: Only preserve existing values if we're not switching between predefined scales
      const isChangingBetweenPredefinedScales = 
        (prevScale === 5 && newScale === 7) || 
        (prevScale === 7 && newScale === 5);
      
      if (!isChangingBetweenPredefinedScales) {
        // Preserve existing label values where possible, including empty strings
        for (let i = 0; i < Math.min(newScale, currentLabels.length); i++) {
          if (currentLabels[i] !== undefined && currentLabels[i] !== null) {
            newLabels[i] = currentLabels[i];
          }
        }
      }
      
      console.log('Final labels to set:', [...newLabels]);
      
      // Update the labels
      onChange("scaleLabels", newLabels);
    }
  }, [properties.scale, componentType, onChange, properties.scaleLabels]);

  /**
   * Only sync options when the category actually changes
   */
  useEffect(() => {
    // Only run if category has actually changed
    if (properties.category !== prevCategoryRef.current) {
      // Update our reference
      prevCategoryRef.current = properties.category;
      
      if (properties.category) {
        const categoryObj = categories.find((c) => c.name === properties.category);
        if (categoryObj) {
          // Sync options when category changes
          onChange("options", categoryObj.options);
        }
      }
    }
  }, [properties.category, categories, onChange]);

  return (
    <EditorContainer>
      {Object.entries(definition).map(([propName, propSchema]) => {
        const value = properties[propName];
        const type = propSchema.type;
        const label = propSchema.label || propName;
        const description = propSchema.description || "";

        let renderedComponent;

        switch (type) {
          /**
           * For "string": check if it's the special "category" field in a dropdown
           */
          case "string": {
            if (propName === "category" && renderingComponentType === "dropdown") {
              // Show CategorySelector for the category property
              renderedComponent = (
                <FieldWrapper key={propName}>
                  <CategorySelector
                    categories={categories}
                    currentValue={value || ""}
                    onSelect={(catName) => onChange("category", catName)}
                    addCategory={handleNewCategory}
                  />
                  {description && <DescriptionText>{description}</DescriptionText>}
                  
                  {/* Category Type Selection Dialog */}
                  {showCategoryTypeSelect && (
                    <CategoryTypeDialog>
                      <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
                        Select category type
                      </Text>
                      <SelectWrapper>
                        <SelectBox
                          options={categoryTypeOptions}
                          value={newCategoryType}
                          onSelect={(value) => setNewCategoryType(value)}
                          width="100%"
                        />
                      </SelectWrapper>
                      <CategoryTypeButtons>
                        <CancelButton 
                          onClick={() => {
                            setShowCategoryTypeSelect(false);
                            setNewCategoryName("");
                          }}
                        >
                          Cancel
                        </CancelButton>
                        <ConfirmButton 
                          onClick={handleConfirmNewCategory}
                        >
                          Create
                        </ConfirmButton>
                      </CategoryTypeButtons>
                    </CategoryTypeDialog>
                  )}
                </FieldWrapper>
              );
            } else {
              // Otherwise, it's a normal text input
              renderedComponent = (
                <FieldWrapper key={propName}>
                  <TextInput
                    label={label}
                    value={value || ""}
                    // Pass the new string to onChange, not the event
                    onChange={(newValue) => onChange(propName, newValue)}
                  />
                  {description && <DescriptionText>{description}</DescriptionText>}
                </FieldWrapper>
              );
            }
            break;
          }

          /**
           * For "boolean": show a toggle
           */
          case "boolean": {
            // Only show "Allow multiple selections" for text type categories
            if (propName === "Allow multiple selections") {
              const categoryObj = categories.find(
                (c) => c.name === properties.category
              );
              
              // Skip rendering this toggle if the selected category is a date type
              if (categoryObj && categoryObj.type === "date") {
                renderedComponent = null;
                break;
              }
            }
            
            // Only show "Add 'Other (specify own)' option" for text type categories
            if (propName === "Add 'Other (specify own)' option") {
              const categoryObj = categories.find(
                (c) => c.name === properties.category
              );
              
              // Skip rendering this toggle if the selected category is a date type or if no category is selected
              if (!categoryObj || categoryObj.type === "date") {
                renderedComponent = null;
                break;
              }
            }
            
            renderedComponent = (
              <FieldWrapper key={propName}>
                <ToggleItem
                  title={label}
                  description={description}
                  isActive={!!value}
                  onToggle={(newValue) => onChange(propName, newValue)}
                />
              </FieldWrapper>
            );
            break;
          }

          /**
           * For "select": show a SelectBox with defined options
           */
          case "select": {
            const optionsArray = propSchema.options || [];
            
            // Special handling for scale property
            if (propName === 'scale') {
              // Create select options as expected by SelectBox
              const selectOptions = optionsArray.map((opt) => ({
                label: String(opt),
                value: opt, // Use original value without conversion
              }));
              
              // Use direct value from properties or default from schema
              const scaleValue = properties.scale !== undefined ? properties.scale : propSchema.default;
              
              renderedComponent = (
                <FieldWrapper key={propName}>
                  <ScaleDropdown
                    scale={scaleValue}
                    onChange={(selected) => {
                      // Store the selected value directly
                      onChange(propName, selected);
                    }}
                    label={label}
                    options={selectOptions.map(opt => opt.value)}
                  />
                  {description && (
                    <DescriptionText
                      typographyStyle={typography.smallText.regular}
                      color={colors.text.gray}
                    >
                      {description}
                    </DescriptionText>
                  )}
                </FieldWrapper>
              );
            } else {
              // Normal select handling for non-scale properties
              const selectOptions = optionsArray.map((opt) => ({
                label: String(opt),
                value: opt,
              }));
              
              renderedComponent = (
                <FieldWrapper key={propName}>
                  <SelectBox
                    label={label}
                    options={selectOptions}
                    value={value !== null && value !== undefined ? value : propSchema.default}
                    onSelect={(selectedVal) => onChange(propName, selectedVal)}
                    width="100%"
                  />
                  {description && (
                    <DescriptionText
                      typographyStyle={typography.smallText.regular}
                      color={colors.text.gray}
                    >
                      {description}
                    </DescriptionText>
                  )}
                </FieldWrapper>
              );
            }
            break;
          }

          /**
           * For "array": standard "add/remove string items" UI
           */
          case "array": {
            const arrValue = Array.isArray(value) ? value : [];
            renderedComponent = (
              <FieldWrapper key={propName}>
                <ArrayHeader>
                  <Text
                    typographyStyle={typography.caption.semibold}
                    color={colors.text.dark}
                  >
                    {label}
                  </Text>
                  {description && (
                    <DescriptionText
                      typographyStyle={typography.smallText.regular}
                      color={colors.text.gray}
                    >
                      {description}
                    </DescriptionText>
                  )}
                </ArrayHeader>

                {arrValue.map((item, idx) => (
                  <ArrayItemRow key={idx}>
                    <TextInput
                      value={item}
                      onChange={(newVal) => {
                        const copy = [...arrValue];
                        copy[idx] = newVal;
                        onChange(propName, copy);
                      }}
                      typographyStyle={typography.body.regular}
                    />
                    <RemoveButton
                      onClick={() => {
                        const copy = [...arrValue];
                        copy.splice(idx, 1);
                        onChange(propName, copy);
                      }}
                    >
                      <Text
                        typographyStyle={typography.smallText.semibold}
                        color={colors.background.white}
                      >
                        Remove
                      </Text>
                    </RemoveButton>
                  </ArrayItemRow>
                ))}

                <AddButton
                  onClick={() => {
                    onChange(propName, [
                      ...arrValue,
                      `Option ${arrValue.length + 1}`,
                    ]);
                  }}
                >
                  <Text
                    typographyStyle={typography.smallText.semibold}
                    color={colors.background.white}
                  >
                    + Add Option
                  </Text>
                </AddButton>
              </FieldWrapper>
            );
            break;
          }

          /**
           * "selectOptions" means the property is an array that comes from a chosen category
           */
          case "selectOptions": {
            const categoryObj = categories.find(
              (c) => c.name === properties.category
            );
            if (!properties.category || !categoryObj) {
              // If no valid category is chosen, we can't show the CategoryOptionsEditor
              renderedComponent = (
                <FieldWrapper key={propName}>
                  <Text>Please select or create a category first.</Text>
                </FieldWrapper>
              );
              break;
            }
            
            // If this is a date type category, display different UI
            if (categoryObj.type === "date") {
              renderedComponent = (
                <FieldWrapper key={propName}>
                  <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
                    Date Selection: {categoryObj.name}
                  </Text>
                  <Text typographyStyle={typography.smallText.regular} color={colors.text.gray} style={{ marginTop: '8px' }}>
                    This is a date selection category. Users will be able to select dates from a calendar.
                  </Text>
                </FieldWrapper>
              );
              break;
            }
            
            // For text type categories, show the options editor
            // Pass the "other option" boolean to the category options
            const extendedCategoryObj = {
              ...categoryObj,
              // Add the "Other option" toggle value to the category object
              hasOtherOption: properties["Add 'Other (specify own)' option"] || false
            };

            renderedComponent = (
              <FieldWrapper key={propName}>
                <CategoryOptionsEditor
                  categoryObj={extendedCategoryObj}
                  options={Array.isArray(value) ? value : []}
                  onChange={(newOptions) => handleOptionsChange(propName, newOptions)}
                  addOptionToCategory={addOptionToCategory}
                  removeOptionFromCategory={removeOptionFromCategory}
                />
              </FieldWrapper>
            );
            break;
          }

          /**
           * "likertLabels" is a special type for handling Likert scale labels
           */
          case "likertLabels": {
            // Get the current scale value (ensure it's a number)
            const numericScale = Number(properties.scale) || 5;
            const scale = parseInt(numericScale, 10);
            
            // Ensure we have an array of the correct length
            const scaleLabels = Array.isArray(value) ? value : [];
            
            // Check if emoji mode is enabled
            const useEmojis = !!properties.useEmojis;
            
            // Ensure we have an emoji array of the correct length if emoji mode is enabled
            const scaleEmojis = useEmojis && Array.isArray(properties.scaleEmojis) ? 
              properties.scaleEmojis : Array(scale).fill(null);
            
            // Make sure emoji array is the correct length when in emoji mode
            if (useEmojis) {
              while (scaleEmojis.length < scale) {
                scaleEmojis.push(null);
              }
            }
            
            // Check if we need to apply proper defaults for a 7-point scale
            if (scale === 7 && scaleLabels.length === 7) {
              const has5PointDefaults = 
                scaleLabels.includes("Strongly Disagree") && 
                scaleLabels.includes("Disagree") && 
                scaleLabels.includes("Neutral") && 
                scaleLabels.includes("Agree") && 
                scaleLabels.includes("Strongly Agree");
              
              // Check for duplicate labels that would indicate improper defaults
              const hasDuplicates = new Set(scaleLabels).size !== scaleLabels.length;
              
              if (has5PointDefaults && hasDuplicates) {
                console.log('Fixing improper 7-point scale defaults');
                // Apply proper 7-point defaults
                onChange("scaleLabels", [
                  "Strongly Disagree",
                  "Moderately Disagree", 
                  "Slightly Disagree",
                  "Neutral", 
                  "Slightly Agree",
                  "Moderately Agree", 
                  "Strongly Agree"
                ]);
                // Return early - the component will re-render with fixed values
                return null;
              }
            }
            
            // Create a labels array of the right size
            const defaultLabels = new Array(scale);
            
            // Fill in the labels array, only use defaults for undefined/null values
            for (let i = 0; i < scale; i++) {
              // Check if the value exists in the scaleLabels array
              if (i < scaleLabels.length) {
                // Keep the current value regardless of what it is (including empty string)
                defaultLabels[i] = (scaleLabels[i] === undefined || scaleLabels[i] === null) 
                  ? `Option ${i + 1}` 
                  : scaleLabels[i];
              } else {
                // For indexes beyond current array length
                defaultLabels[i] = `Option ${i + 1}`;
              }
            }
            
            renderedComponent = (
              <FieldWrapper key={propName}>
                <ArrayHeader>
                  <HeaderRow>
                    <Text
                      typographyStyle={typography.caption.semibold}
                      color={colors.text.dark}
                    >
                      Scale Labels
                    </Text>
                    <ToggleItem
                      title="Use Emojis"
                      isActive={useEmojis}
                      onToggle={toggleEmojiMode}
                      compact={true}
                    />
                  </HeaderRow>
                  <DescriptionText
                    typographyStyle={typography.smallText.regular}
                    color={colors.text.gray}
                  >
                    Define labels{useEmojis ? " and emojis" : ""} for each point on the scale
                  </DescriptionText>
                </ArrayHeader>

                {defaultLabels.map((label, idx) => (
                  <div key={idx} style={{ marginBottom: '24px' }}>
                    <ArrayItemRow>
                      <Text style={{ width: '24px', flexShrink: 0 }}>
                        {idx + 1}:
                      </Text>
                      <TextInput
                        value={label}
                        onChange={(newVal) => {
                          const copy = [...defaultLabels];
                          copy[idx] = newVal;
                          onChange(propName, copy);
                        }}
                        typographyStyle={typography.body.regular}
                      />
                      {useEmojis && (
                        <StyledEmojiButton 
                          onClick={(event) => toggleEmojiPicker(idx, event)}
                        >
                          {scaleEmojis[idx] || '+'} 
                        </StyledEmojiButton>
                      )}
                    </ArrayItemRow>
                    
                    {/* Inline emoji picker */}
                    {useEmojis && activeEmojiRowIndex === idx && (
                      <div style={{ 
                        marginTop: '8px', 
                        marginLeft: '0', 
                        width: '100%', 
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: `2px solid ${colors.primary[500]}`,
                        padding: '8px',
                        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.25)'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          marginBottom: '8px'
                        }}>
                          <Text 
                            typographyStyle={typography.caption.semibold} 
                            color={colors.text.dark}
                          >
                            Select Emoji for Option {idx + 1}
                          </Text>
                          <button
                            onClick={() => setActiveEmojiRowIndex(null)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        <CustomEmojiPicker 
                          onEmojiClick={(emojiData) => handleEmojiClick(emojiData, idx)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </FieldWrapper>
            );
            break;
          }

          // Fallback if the JSON schema type is unknown
          default:
            renderedComponent = (
              <FieldWrapper key={propName}>
                <Text
                  typographyStyle={typography.caption.semibold}
                  color={colors.text.dark}
                >
                  {label}
                </Text>
                <UnsupportedTypeText
                  typographyStyle={typography.smallText.regular}
                  color={colors.auxiliary.red[500]}
                >
                  Unsupported property type: {type}
                </UnsupportedTypeText>
              </FieldWrapper>
            );
        }

        // Return the rendered component if it's not null
        return renderedComponent;
      })}
    </EditorContainer>
  );
};

export default PropertyEditor;

/** -- styled components -- **/
const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldWrapper = styled.div`
  margin-bottom: 16px;
  position: relative;
`;

const DescriptionText = styled(Text)`
  margin-top: 4px;
`;

const UnsupportedTypeText = styled(Text)`
  margin-top: 4px;
  color: ${colors.auxiliary.red[500]};
`;

const ArrayHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const ArrayItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const AddButton = styled.button`
  background: ${colors.primary[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  &:hover {
    background: ${colors.primary[600]};
  }
`;

const RemoveButton = styled.button`
  background: ${colors.auxiliary.red[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  &:hover {
    background: ${colors.auxiliary.red[600]};
  }
`;

// Category Type Dialog styles
const CategoryTypeDialog = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: ${colors.background.white};
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid ${colors.neutral[300]};
`;

const SelectWrapper = styled.div`
  margin-top: 12px;
  margin-bottom: 8px;
`;

const CategoryTypeButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  gap: 8px;
`;

const CancelButton = styled.button`
  background: ${colors.neutral[200]};
  color: ${colors.text.dark};
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  &:hover {
    background: ${colors.neutral[300]};
  }
`;

const ConfirmButton = styled.button`
  background: ${colors.primary[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  &:hover {
    background: ${colors.primary[600]};
  }
`;

const StyledEmojiButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-left: 8px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 4px;
  background-color: white;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${colors.neutral[100]};
  }
`;
